// Link in email can be changed here: https://console.firebase.google.com/u/3/project/astrius-dev/authentication/emails
import { applyActionCode, verifyPasswordResetCode } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import { doc, setDoc } from 'firebase/firestore';

import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../firebase';
import { PlasmicGetStarted2 } from './plasmic/collect_wise_draft/PlasmicGetStarted2';
import LoadingSpinner from './LoadingSpinner';
import CreateNewPassword from './CreateNewPassword';
import { getErrorMessage } from '../common/errorUtils';

const VerifyEmail: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  // ?mode=action&oobCode=code
  const navigate = useNavigate();
  const [actionCode, setActionCode] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState<boolean>(false);
  const [verifyError, setVerifyError] = useState<Error | null>(null);
  const location = useLocation();

  const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
    e.preventDefault();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const mode = searchParams.get('mode');
    const actionCode = searchParams.get('oobCode');
    setActionCode(actionCode);

    if (actionCode == null) {
      setHasError(true);
      setLoading(false);
      setVerifyError(new Error('No action code provided in verification link'));
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, actionCode)
        .then(async () => {
          // now set the state to the plasmic wrapper page to enter the new password and call confirmPasswordReset as described here https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#confirmpasswordreset
          setResetPassword(true);
          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
          setHasError(true);
          setVerifyError(error);
        });
    } else if (mode === 'verifyEmail') {
      applyActionCode(auth, actionCode)
        .then(async () => {
          if (currentUser != null) {
            window.addEventListener('beforeunload', handleBeforeUnload);

            const docId = uuidv4();

            await setDoc(doc(db, 'userInfo', docId), {
              user: currentUser.uid,
              email: currentUser.email,
              identityVerification: false,
              autoEnrollDebtors: false,
              invoiceNum: 1,
              date: new Date(),
              customPrice: 0,
              customLimit: 0,
              monthlyAmount: 0,
              cardApproval: false,
              heartlandPublicKey: '',
              heartlandSecretKey: '',
              fraudCount: 0,
              merchantName: '',
              collectionsEmail: '',
              requiresRNDCheck: false,
              isAgency: false,
              customWorkflowID: '',
            });

            window.removeEventListener('beforeunload', handleBeforeUnload);

            await currentUser.reload();
            navigate('/get-started');
          } else {
            setLoading(false);
            setHasError(true);
            setVerifyError(new Error('User is not logged in.'));
          }
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
          setHasError(true);
          setVerifyError(error);
        });
    } else {
      setHasError(true);
      setLoading(false);
      setVerifyError(new Error('Invalid verification mode'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return (
      <div>
        <div>Invalid Verification Link</div>
        {verifyError && (
          <div style={{ marginTop: '10px', color: 'red', fontSize: '14px' }}>
            Error: {getErrorMessage(verifyError)}
          </div>
        )}
      </div>
    );
  } else if (resetPassword) {
    return <CreateNewPassword code={actionCode} />;
  } else {
    if (isMobile) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const GetStartedComponent = PlasmicGetStarted2 as any;
      return <GetStartedComponent />;
    } else {
      return (
        <div>
          Your email has been verified. Please return to the site and login
          again to setup your profile.
        </div>
      );
    }
  }
};

export default VerifyEmail;
